import React, { useState } from 'react';
import { X, Building2, Camera, Trash2, CheckCircle2, Search, RefreshCw } from 'lucide-react';
import { setoresService, type Setor } from '../../services/setores.service';
import FormattedInput from '../FormattedInput';
import CepInput from '../CepInput';
import StateSelect from '../StateSelect';
import { useToast } from '../Toast';

interface ModalCriarSetorProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId?: number;
  onCreated: (novoSetor: Setor) => void;
}

export function ModalCriarSetor({
  isOpen,
  onClose,
  empresaId,
  onCreated,
}: ModalCriarSetorProps) {
  const toast = useToast();

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [fotos, setFotos] = useState<Array<{ id?: string; url: string; nome: string; dataUrl?: string }>>([]);

  // Prestação de Serviços
  const [isPrestacaoServico, setIsPrestacaoServico] = useState(false);
  const [postoTomador, setPostoTomador] = useState('');
  const [postoCnpj, setPostoCnpj] = useState('');
  const [postoCep, setPostoCep] = useState('');
  const [postoLogradouro, setPostoLogradouro] = useState('');
  const [postoNumero, setPostoNumero] = useState('');
  const [postoComplemento, setPostoComplemento] = useState('');
  const [postoBairro, setPostoBairro] = useState('');
  const [postoCidade, setPostoCidade] = useState('');
  const [postoUf, setPostoUf] = useState('SP');

  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [cnpjFeedback, setCnpjFeedback] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sucessoCriacao, setSucessoCriacao] = useState(false);

  if (!isOpen) return null;

  const handleFotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        toast.error(`O arquivo "${file.name}" não é uma imagem válida.`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`A imagem "${file.name}" excede o limite de 10MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setFotos((prev) => [
          ...prev,
          {
            id: `foto_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
            url: base64,
            dataUrl: base64,
            nome: file.name,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });

    if (e.target) e.target.value = '';
  };

  const handleRemoverFoto = (index: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConsultarCnpjTomador = async (val?: string) => {
    const raw = (val !== undefined ? val : postoCnpj).replace(/\D/g, '');
    if (raw.length !== 14) return;

    setLoadingCnpj(true);
    setCnpjFeedback(null);

    try {
      // 1. Tentar BrasilAPI diretamente
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${raw}`);
      if (res.ok) {
        const d = await res.json();
        const razao = d.razao_social || d.nome_fantasia || '';
        if (razao) setPostoTomador(razao);
        if (d.logradouro) setPostoLogradouro(d.logradouro);
        if (d.numero) setPostoNumero(d.numero);
        if (d.complemento) setPostoComplemento(d.complemento);
        if (d.bairro) setPostoBairro(d.bairro);
        if (d.municipio) setPostoCidade(d.municipio);
        if (d.uf) setPostoUf(d.uf);
        if (d.cep) setPostoCep(d.cep.replace(/\D/g, ''));
        setCnpjFeedback('Dados do tomador carregados via Receita Federal');
        toast.success('Dados do tomador carregados via Receita Federal.');
        return;
      }

      // 2. Fallback para publica.cnpj.ws
      const res2 = await fetch(`https://publica.cnpj.ws/cnpj/${raw}`);
      if (res2.ok) {
        const d2 = await res2.json();
        const est = d2.estabelecimento || {};
        const razao = d2.razao_social || est.nome_fantasia || '';
        if (razao) setPostoTomador(razao);
        if (est.logradouro) setPostoLogradouro(est.logradouro);
        if (est.numero) setPostoNumero(est.numero);
        if (est.complemento) setPostoComplemento(est.complemento);
        if (est.bairro) setPostoBairro(est.bairro);
        if (est.cidade?.nome) setPostoCidade(est.cidade.nome);
        if (est.estado?.sigla) setPostoUf(est.estado.sigla);
        if (est.cep) setPostoCep(est.cep.replace(/\D/g, ''));
        setCnpjFeedback('Dados do tomador carregados via Receita Federal');
        toast.success('Dados do tomador carregados via Receita Federal.');
        return;
      }

      setCnpjFeedback('CNPJ consultado, mas dados automáticos indisponíveis.');
    } catch {
      setCnpjFeedback('CNPJ consultado, mas dados automáticos indisponíveis.');
    } finally {
      setLoadingCnpj(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nome.trim()) {
      toast.error('Informe o nome do setor.');
      return;
    }

    if (!descricao.trim()) {
      toast.error('Informe a descrição do setor.');
      return;
    }

    if (isPrestacaoServico) {
      if (!postoTomador.trim()) {
        toast.error('Informe a razão social do tomador de serviço.');
        return;
      }
      if (!postoLogradouro.trim() || !postoNumero.trim() || !postoBairro.trim() || !postoCidade.trim()) {
        toast.error('Preencha os campos obrigatórios de endereço do posto de trabalho.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        nome: nome.trim(),
        descricao: descricao.trim(),
        empresa_id: empresaId,
        fotos: fotos.length > 0 ? fotos : undefined,
        is_prestacao_servico: isPrestacaoServico,
        posto: isPrestacaoServico
          ? {
              razao_social_cliente: postoTomador.trim(),
              cnpj_cliente: postoCnpj.replace(/\D/g, ''),
              cep: postoCep.replace(/\D/g, '') || undefined,
              logradouro: postoLogradouro.trim(),
              numero: postoNumero.trim(),
              complemento: postoComplemento.trim() || undefined,
              bairro: postoBairro.trim(),
              cidade: postoCidade.trim(),
              uf: postoUf,
            }
          : undefined,
      };

      const novoSetor = await setoresService.create(payload);
      setSucessoCriacao(true);
      onCreated(novoSetor);
    } catch (err: unknown) {
      console.error('Erro ao cadastrar setor:', err);
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = errObj?.response?.data?.message || errObj?.message || 'Falha ao cadastrar setor.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao cadastrar setor.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setNome('');
    setDescricao('');
    setFotos([]);
    setIsPrestacaoServico(false);
    setPostoTomador('');
    setPostoCnpj('');
    setPostoCep('');
    setPostoLogradouro('');
    setPostoNumero('');
    setPostoComplemento('');
    setPostoBairro('');
    setPostoCidade('');
    setPostoUf('SP');
    setCnpjFeedback(null);
    setSucessoCriacao(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-primary-50 text-primary-700 rounded-xl border border-primary-100">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Novo Setor</h3>
              <p className="text-xs text-gray-500 font-medium">
                Estrutura organizacional e ambiental
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleResetAndClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        {sucessoCriacao ? (
          <div className="p-8 text-center space-y-4 my-auto">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div className="space-y-2">
              <h4 className="text-lg font-bold text-gray-900">
                Setor cadastrado com sucesso
              </h4>
              <div className="text-sm text-gray-600 max-w-sm mx-auto space-y-2">
                <p>
                  O setor <strong>"{nome}"</strong> foi incorporado à estrutura da empresa e já está disponível para alocação de cargos e colaboradores.
                </p>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-left text-xs text-emerald-950 space-y-1">
                  <strong className="text-emerald-900 font-bold flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-600 shrink-0" />
                    <span>Esteira de Laudos em Andamento:</span>
                  </strong>
                  <p className="text-[11px] text-emerald-800">
                    A inclusão do novo ambiente foi registrada na esteira técnica para atualização formal do PGR e PCMSO em até <strong>5 dias úteis</strong>.
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-4">
              <button
                type="button"
                onClick={handleResetAndClose}
                className="w-full sm:w-auto px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl shadow-md transition-all cursor-pointer"
              >
                Concluir e Voltar ao Organograma
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
            {/* Nome do Setor */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nome do Setor <span className="text-red-500 font-bold">*</span>
              </label>
              <input
                type="text"
                required
                maxLength={100}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Administrativo, Cozinha, Operacional, Logística"
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800"
              />
            </div>

            {/* Descrição do Setor */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Descrição do Setor <span className="text-red-500 font-bold">*</span>
              </label>
              <textarea
                required
                rows={3}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva as características do ambiente, finalidade ou atividades desenvolvidas neste setor (obrigatório para o PGR / NR-01)..."
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800"
              />
            </div>

            {/* Evidências Fotográficas do Setor */}
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                  <Camera className="w-4 h-4 text-primary-600" />
                  <span>Evidências Fotográficas do Setor</span>
                </label>
                <span className="text-[11px] font-normal text-gray-500">
                  {fotos.length} foto(s) anexada(s)
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Adicione fotos reais do ambiente de trabalho (instalações, máquinas, postos) para incorporação ao PGR.
              </p>

              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-semibold cursor-pointer transition">
                  <Camera className="w-3.5 h-3.5 text-slate-500" />
                  <span>+ Adicionar Imagens</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFotoSelect}
                  />
                </label>
                <span className="text-[11px] text-gray-400">
                  {fotos.length === 0 ? 'Nenhuma foto anexada ainda' : 'Você pode adicionar mais fotos se desejar'}
                </span>
              </div>

              {/* Grid de Thumbnails */}
              {fotos.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {fotos.map((foto, idx) => (
                    <div
                      key={foto.id || idx}
                      className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-100 aspect-video flex items-center justify-center shadow-2xs"
                    >
                      <img
                        src={foto.url || foto.dataUrl}
                        alt={foto.nome || `Foto ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between p-1 text-white">
                        <span className="text-[10px] truncate max-w-[80px]" title={foto.nome}>
                          {foto.nome}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoverFoto(idx)}
                          className="p-1 rounded bg-red-600/90 hover:bg-red-700 text-white cursor-pointer transition"
                          title="Remover foto"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Toggle de Prestação de Serviços */}
            <div className="pt-2 border-t border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isPrestacaoServico}
                  onChange={(e) => setIsPrestacaoServico(e.target.checked)}
                  className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="text-xs font-bold text-gray-800">
                  Prestação de Serviços em Estabelecimento de Terceiro (Posto de Trabalho Externo)
                </span>
              </label>
            </div>

            {/* Dados do Posto de Trabalho Externo */}
            {isPrestacaoServico && (
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-primary-600" />
                    Dados do Tomador / Posto de Trabalho Externo:
                  </p>
                  <span className="text-[10px] text-primary-700 font-semibold bg-primary-50 px-2 py-0.5 rounded-full border border-primary-200">
                    NR-01 Item 1.5.8
                  </span>
                </div>

                {/* CNPJ */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    CNPJ do Tomador
                  </label>
                  <div className="flex gap-2">
                    <FormattedInput
                      type="cnpj"
                      value={postoCnpj}
                      onChange={(val) => {
                        setPostoCnpj(val);
                        const clean = val.replace(/\D/g, '');
                        if (clean.length === 14) {
                          handleConsultarCnpjTomador(clean);
                        }
                      }}
                      placeholder="00.000.000/0000-00"
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleConsultarCnpjTomador()}
                      disabled={loadingCnpj || postoCnpj.replace(/\D/g, '').length !== 14}
                      className="px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-medium border border-slate-300 disabled:opacity-50 transition shrink-0 cursor-pointer flex items-center gap-1"
                      title="Consultar dados cadastrais na Receita Federal"
                    >
                      {loadingCnpj ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Search className="w-3.5 h-3.5" />
                      )}
                      <span>{loadingCnpj ? 'Buscando...' : 'Buscar CNPJ'}</span>
                    </button>
                  </div>
                  {cnpjFeedback && (
                    <p className="text-[11px] text-emerald-700 font-medium mt-1">
                      {cnpjFeedback}
                    </p>
                  )}
                </div>

                {/* Razão Social */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Razão Social / Nome do Cliente Tomador <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    required={isPrestacaoServico}
                    placeholder="Ex: Contratante XYZ Logística S/A"
                    value={postoTomador}
                    onChange={(e) => setPostoTomador(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>

                {/* CEP e Logradouro */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      CEP <span className="text-red-500 font-bold">*</span>
                    </label>
                    <CepInput
                      value={postoCep}
                      onChange={(val) => setPostoCep(val)}
                      onAddressFound={(endereco) => {
                        if (endereco.logradouro) setPostoLogradouro(endereco.logradouro);
                        if (endereco.bairro) setPostoBairro(endereco.bairro);
                        if (endereco.localidade) setPostoCidade(endereco.localidade);
                        if (endereco.uf) setPostoUf(endereco.uf);
                      }}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Logradouro <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      required={isPrestacaoServico}
                      placeholder="Ex: Av. das Nações Unidas"
                      value={postoLogradouro}
                      onChange={(e) => setPostoLogradouro(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                </div>

                {/* Número e Complemento */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Número <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      required={isPrestacaoServico}
                      placeholder="Ex: 1000 ou S/N"
                      value={postoNumero}
                      onChange={(e) => setPostoNumero(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Complemento
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Galpão 3, Bloco A"
                      value={postoComplemento}
                      onChange={(e) => setPostoComplemento(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                </div>

                {/* Bairro, Cidade e UF */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Bairro <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      required={isPrestacaoServico}
                      placeholder="Ex: Pinheiros"
                      value={postoBairro}
                      onChange={(e) => setPostoBairro(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Cidade <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      required={isPrestacaoServico}
                      placeholder="Ex: São Paulo"
                      value={postoCidade}
                      onChange={(e) => setPostoCidade(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Estado / UF <span className="text-red-500 font-bold">*</span>
                    </label>
                    <StateSelect
                      value={postoUf}
                      onChange={(val) => setPostoUf(val)}
                      required={isPrestacaoServico}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Banner Informativo de SLA */}
            <div className="p-3 bg-emerald-50/80 border border-emerald-200 rounded-xl flex items-start gap-2.5 text-xs text-emerald-950 leading-relaxed">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-600 shrink-0 mt-1 shadow-2xs" />
              <div>
                <strong className="text-emerald-950 font-bold">Esteira Automática de Laudos:</strong> A inclusão deste setor enviará uma solicitação de atualização de ambiente no PGR e PCMSO para a equipe técnica com prazo de atendimento de <strong>5 dias úteis</strong>.
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleResetAndClose}
                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-xl shadow-sm transition cursor-pointer"
              >
                {isSubmitting ? 'Salvando...' : 'Salvar Setor'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
